/**
 * ADMIN MODULE — sidebar navigation.
 * Visible only within admin routes (the whole module is gated by
 * RequireSuperAdmin). Shows the live Paperclip connection status at the bottom
 * and a way back to the main app.
 */

import { NavLink } from "react-router-dom";
import { usePaperclipHealth } from "../hooks/usePaperclipHealth.js";

const ICONS = {
  dashboard: (
    <svg viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="9" rx="1.5" /><rect x="14" y="3" width="7" height="5" rx="1.5" /><rect x="14" y="12" width="7" height="9" rx="1.5" /><rect x="3" y="16" width="7" height="5" rx="1.5" /></svg>
  ),
  agents: (
    <svg viewBox="0 0 24 24"><rect x="5" y="8" width="14" height="11" rx="2" /><path d="M12 8V4M9 4h6M9 13h.01M15 13h.01M9.5 16h5" /></svg>
  ),
  linkedin: (
    <svg viewBox="0 0 24 24"><path d="M4 19V5M4 19h16M8 19v-6M12 19v-9M16 19v-4" /></svg>
  ),
  seo: (
    <svg viewBox="0 0 24 24"><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></svg>
  ),
  tickets: (
    <svg viewBox="0 0 24 24"><path d="M4 5h16v5a2 2 0 0 0 0 4v5H4v-5a2 2 0 0 0 0-4z" /><path d="M14 5v14" /></svg>
  ),
  email: (
    <svg viewBox="0 0 24 24"><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></svg>
  ),
  nurture: (
    <svg viewBox="0 0 24 24"><path d="M22 12c0 5.5-4.5 10-10 10S2 17.5 2 12 6.5 2 12 2a8 8 0 0 1 8 8c0 4-3 6-6 6a4 4 0 0 1-4-4c0-2.5 2-4 4-4" /></svg>
  ),
  team: (
    <svg viewBox="0 0 24 24"><circle cx="9" cy="8" r="3" /><path d="M3.5 19a5.5 5.5 0 0 1 11 0" /><path d="M16 6a3 3 0 0 1 0 6M17.5 19a5.5 5.5 0 0 0-3-4.9" /></svg>
  ),
  modules: (
    <svg viewBox="0 0 24 24"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><path d="M14 17.5h7M17.5 14v7" /></svg>
  ),
};

const LINKS = [
  { to: "/admin", label: "Dashboard", icon: ICONS.dashboard, end: true },
  { to: "/admin/modules", label: "Modules", icon: ICONS.modules },
  { to: "/admin/tickets", label: "Support Tickets", icon: ICONS.tickets },
  { to: "/admin/email", label: "App Email Settings", icon: ICONS.email },
  { to: "/admin/team", label: "Team", icon: ICONS.team },
  { to: "/admin/agents", label: "Agents", icon: ICONS.agents },
  { to: "/admin/linkedin", label: "LinkedIn Flywheel", icon: ICONS.linkedin },
  { to: "/admin/nurture", label: "Email Nurture", icon: ICONS.nurture },
  { to: "/admin/seo", label: "SEO Rank", icon: ICONS.seo },
];

export default function AdminNav() {
  const health = usePaperclipHealth();
  const online = health === "online";

  return (
    <nav className="admin-nav" aria-label="Admin">
      <div className="admin-nav-head">
        <span className="admin-pill">Admin</span>
      </div>

      {LINKS.map((l) => (
        <NavLink
          key={l.to}
          to={l.to}
          end={l.end}
          className={({ isActive }) => `admin-nav-link${isActive ? " active" : ""}`}
        >
          {l.icon}
          <span>{l.label}</span>
        </NavLink>
      ))}

      <div className="admin-nav-spacer" />

      <div className="admin-nav-foot">
        <span className="admin-conn" title={`Paperclip: ${health}`}>
          <span className={`admin-dot ${online ? "online" : health === "pending" ? "pending" : "offline"}`} />
          {online ? "Agents connected" : health === "pending" ? "Checking agents…" : "Agents offline"}
        </span>
      </div>
    </nav>
  );
}
